import React, { useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import I18n from 'modules/i18n';
import Filter, { FilterCategory } from '@/components/common/libraries/common/Filter';
import Checkbox from '@/components/common/libraries/common/Checkbox';
import { LessonFilterOption, UnitFilterOption } from '../types';
import RowChevron from '../RowChevron';
import SectionHeading from '../SectionHeading';
import TruncatedCorrelationsList, { truncationChildrenAttrs, truncationRowAttrs } from '../TruncatedCorrelationsList';
import useCorrelationsStore from '../useCorrelationsStore';

const COLLAPSED_MAX_HEIGHT = 210;

interface LessonFilterProps {
  units: UnitFilterOption[];
}

const lessonLabel = ({ title, titleNumber }: LessonFilterOption) => (
  titleNumber ? `L${titleNumber} - ${title}` : title
);

interface UnitGroupProps {
  unit: UnitFilterOption;
}

// Same row layout as StandardsTreeNode (chevron on the left of the checkbox) so the two filters read as one list.
const UnitGroup = ({ unit }: UnitGroupProps) => {
  const [expanded, setExpanded] = useState(false);
  const lessonsCheckedIds = useCorrelationsStore(s => s.lessonsCheckedIds);
  const checkLessons = useCorrelationsStore(s => s.checkLessons);
  const lessonIds = unit.lessons.map(lesson => lesson.id);
  const checkedCount = lessonIds.filter(id => lessonsCheckedIds.has(id)).length;
  const allChecked = lessonIds.length > 0 && checkedCount === lessonIds.length;
  const label = `U${unit.unitNumber} - ${unit.title}`;

  return (
    <Collapsible.Root asChild open={expanded}>
      <li>
        <div
          className="tw-flex tw-items-start tw-gap-1 tw-py-1 tw-pr-2 hover:tw-bg-gray-50 body sm"
          {...truncationRowAttrs({ checked: checkedCount > 0 })}
        >
          <RowChevron expanded={expanded} label={label} onToggle={() => setExpanded(prev => !prev)} />

          <Checkbox
            shouldCover
            checked={allChecked}
            extraClassName="tw-items-start tw-flex-1 tw-min-w-0"
            indeterminate={checkedCount > 0 && !allChecked}
            label={label}
            onChange={event => checkLessons(lessonIds, event.target.checked)}
          />
        </div>

        <Collapsible.Content asChild {...truncationChildrenAttrs()}>
          <ul role="list">
            {unit.lessons.map(lesson => (
              <li key={lesson.id} {...truncationRowAttrs({ checked: lessonsCheckedIds.has(lesson.id) })}>
                <Filter
                  checked={lessonsCheckedIds.has(lesson.id)}
                  className="tw-pl-8"
                  label={lessonLabel(lesson)}
                  value={lesson.id}
                  onChange={(value, checked) => checkLessons([Number(value)], checked)}
                />
              </li>
            ))}
          </ul>
        </Collapsible.Content>
      </li>
    </Collapsible.Root>
  );
};

// The unit/lesson filter under `correlations_filters`: a fixed list from the server's `filter_options`,
// independent of the tags on the page or the standards selection.
const LessonFilter = ({ units }: LessonFilterProps) => {
  const categoryExpanded = useCorrelationsStore(state => state.categoryExpanded);
  const setCategoryExpanded = useCorrelationsStore(state => state.setCategoryExpanded);

  return (
    <FilterCategory
      className="tw-pr-4 tw-mb-1"
      shouldCollapse={false}
      triggerElement={<SectionHeading>{I18n.t('unit')}</SectionHeading>}
    >
      <div aria-label={I18n.t('unit')} className="tw-pl-2" role="group">
        <TruncatedCorrelationsList
          collapsedMaxHeight={COLLAPSED_MAX_HEIGHT}
          expanded={categoryExpanded.unit}
          remeasureKey={categoryExpanded}
          onToggle={next => setCategoryExpanded('unit', next)}
        >
          {/* The list sits inside the clip container so the unit rows stay direct children of
              the <ul> and keep their list semantics (PLAT-17901). */}
          <ul role="list">
            {units.map(unit => <UnitGroup key={unit.id} unit={unit} />)}
          </ul>
        </TruncatedCorrelationsList>
      </div>
    </FilterCategory>
  );
};

export default LessonFilter;
